import type { TradeInfo } from '@carvana/merch-contracts';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isAxiosError } from 'axios';
import { useContext } from 'react';
import { httpWithAuth } from '~/clients';
import { VEHICLE_PRICING } from '~/constants/endpoints';
import CommonStaticContext from '~/context/common/StaticContext';
import TradeContext, {
  useGetAcquisitionNameForApiRequest,
  useIsTradeInApplied
} from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useSoonestRoute } from '~/context/vdp/DeliveryPickupContext';
import VdpStaticContext from '~/context/vdp/StaticContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleId } from '~/context/vdp/VehicleContext';
import { VehiclePaymentTerm } from '~/contracts/vdp';
import useDeliveryPickupLoadedOrWillNotLoad from '~/hooks/vdp/selectors/useDeliveryPickupLoadedOrWillNotLoad';
import useIsHighNetTrade from '~/hooks/vdp/useIsHighNetTrade';
import type { FinanceInfoFromApi } from '~/services/common/contracts';
import {
  nonSearchableError,
  PricingError
} from '~/services/common/contracts/PricingResponse';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

export type VehiclePricingResponse = {
  financeInfo: FinanceInfoFromApi;
  tradeInfo?: TradeInfo;
  vehicleId: number;
  price: Partial<VehiclePaymentTerm>;
  searchRequestId?: string;
  errors: string[];
};

export default function useGetVehiclePricing() {
  const { clientId, eventCorrelationId } = useContext(CommonStaticContext);
  const {
    trades: { isLoaded: isTradesLoaded }
  } = useContext(TradeContext);
  const {
    vehiclePricingInfo: { setData, setLoading, setError },
    fetchVehiclePricingFlag,
    setPricingError
  } = useContext(UserVehicleContext);
  const { utmParams } = useContext(VdpStaticContext);

  const vehicleId = useGetVehicleId();
  const location = useUserLocation();
  const applyTradeIn = useIsTradeInApplied();
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const isHighNetTrade = useIsHighNetTrade();
  const isDeliveryPickupLoadedOrWillNotLoad =
    useDeliveryPickupLoadedOrWillNotLoad();
  const routeId = useSoonestRoute()?.routeId;
  const shouldApplyTradeIn = isHighNetTrade ? false : applyTradeIn;

  useEffectWithController(
    controller => {
      if (!isDeliveryPickupLoadedOrWillNotLoad || !isTradesLoaded) {
        return;
      }

      setLoading();
      setPricingError(PricingError.None);

      httpWithAuth
        .post<VehiclePricingResponse>(
          VEHICLE_PRICING,
          {
            applyVerboseLogging: false,
            applyTradeIn: shouldApplyTradeIn,
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            routeId,
            vehicleId: vehicleId,
            zip5: location.zip,
            preferredAcquisitionName: isHighNetTrade
              ? ''
              : preferredAcquisitionName,
            analyticsData: {
              //this is a required field for the endpoint we can send the rest of the data as empty. Ask the BE team if they need more data in this object.
              clientId,
              correlationId: eventCorrelationId,
              utmParams
            }
          },
          {
            signal: controller.signal
          }
        )
        .then(({ data }) => {
          if (data.errors.length > 0) {
            setPricingError(PricingError.NoVehiclePricingInfo);
          }
          setData(data);
        })
        .catch(
          generateErrorLogger(
            'Get VDP Pricing',
            {},
            {
              callback: error => {
                setError(error);
                // if the vehicle is not in elastic search the endpoint will 404 and return an errorMessage in response
                const errorMessage = isAxiosError(error)
                  ? error.response?.data ?? ''
                  : '';
                setPricingError(
                  errorMessage === nonSearchableError
                    ? PricingError.NonSearchable
                    : PricingError.ApiError
                );
              },
              controller
            }
          )
        );
    },
    [
      applyTradeIn,
      clientId,
      eventCorrelationId,
      fetchVehiclePricingFlag,
      isDeliveryPickupLoadedOrWillNotLoad,
      isHighNetTrade,
      isTradesLoaded,
      location.zip,
      preferredAcquisitionName,
      routeId,
      setData,
      setError,
      setLoading,
      setPricingError,
      shouldApplyTradeIn,
      utmParams,
      vehicleId
    ]
  );
}
